import 'plyr/dist/plyr.css';

import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';

import { FormattedMessage, useIntl } from 'react-intl';
import { Genres, movieDetailsResponse } from '@/logic/services';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { BookmarkName } from '../shared/bookmark-name';
import Drawer from '@mui/material/Drawer';
import Head from 'next/head';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type Plyr from 'plyr';

import { pxToRem } from '@/theme/typography';
import { mediaImageUrl } from '@/logic/helpers/media-image';
import shallow from 'zustand/shallow';

import { useDialog } from '@/logic/state/dialog';
import useFilmFormatter from '@/hooks/use-film-formatter';
import { useSnack } from '@/logic/state/snackbar';
import { CloseButton } from '../shared/close-button';
import MovieInfo from '../shared/movie-details-info';
import ImageBoxFuture from '../shared/image-box-future';

const copiedIcon = '/icons/movie-details/copied.svg';
const shareWhiteIcon = '/icons/shared/share-white.svg';
const timeIcon = '/icons/movie-details/time.svg';

interface MovieBannerProps {
  data: movieDetailsResponse;
}
const videoOptions = {
  controls: [
    'play-large',
    'restart',
    'rewind',
    'play',
    'progress',
    'current-time',
    'duration',
    'mute',
    'volume',
    'captions',
    'settings',
    'pip',
    'airplay',
    'download',
    'start',
    'end',
    'captions',
    'stop',
  ],
  disableContextMenu: false,
};

export const MovieBanner: FC<MovieBannerProps> = movieBannerProps => {
  const theme = useTheme();
  const intl = useIntl();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [playState, setPlayState] = useState(false);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const [drawerState, setDrawerState] = React.useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const videoId = movieBannerProps?.data?.film?.trailerUrl;
  const ref = useRef(null);

  const closeCurrentDialog = (): void => {
    setDialog({});
  };

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setDrawerState(open);
    };

  const checkPlaying = useCallback(() => {
    (ref.current?.plyr as Plyr)?.playing ? setPlayState(true) : setPlayState(false);
  }, []);

  const checkPlaying2 = useCallback(() => {
    (ref.current?.plyr as Plyr)?.play();
    checkPlaying();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { formattedDuration, durationWithRateWithRtl } = useFilmFormatter(
    movieBannerProps?.data?.film
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  /*
    This banner is server-rendered so that crawlers get the movie's <h1> and
    poster in the initial HTML. It used to be imported with `ssr: false`, which
    left the page without either. Only the trailer player stays client-side:
    plyr's ESM build reads `document` at import time, so it is imported here
    rather than at the top of the file. The trailer iframe itself is mounted
    only after hydration (see `isMounted` below). A server-rendered iframe would
    start loading the YouTube embed during HTML parse, and plyr would then
    replace it with its own.
  */
  useEffect(() => {
    if (!isMounted) return;
    let isCancelled = false;
    import('plyr').then(({ default: PlyrPlayer }) => {
      if (!isCancelled) new PlyrPlayer('#player', videoOptions);
    });
    return () => {
      isCancelled = true;
    };
  }, [isMounted]);

  const handleTrailerURL = (link: string): string => {
    let modifiedLink = '';
    //  link.replace('youtube.com', 'youtube-nocookie.com');
    modifiedLink = link.replace('watch?v=', 'embed/');
    if (link.includes('youtu.be/')) {
      modifiedLink = modifiedLink.replace('youtu.be/', 'www.youtube.com/embed/');
    }
    return modifiedLink;
  };

  const coverUrl = movieBannerProps?.data?.film?.coverUrl;
  // The cover is full width. Phones get the 960 rung of the media CDN, laptops
  // and up the 1920 one, instead of the CMS original. On a host that does not
  // resize, both are the original URL.
  const coverMobileUrl = coverUrl && mediaImageUrl(coverUrl, 960);
  const coverDesktopUrl = coverUrl && mediaImageUrl(coverUrl, 1920);
  const coverBackground = (url?: string) =>
    `linear-gradient(180deg, rgba(0, 0, 0, 0.87) 7.33%, rgba(0, 0, 0, 0) 47.54%), linear-gradient(256.27deg, #171A1F 0%, rgba(23, 26, 31, 0) 107.57%)${
      url ? `, url("${url}")` : ''
    }`;

  return (
    <>
      {/*
        The hero cover is painted as a CSS `background-image` in the `sx` below,
        which means the browser cannot see it until emotion has generated and
        parsed that stylesheet at runtime. It is the LCP element on this page,
        and Lighthouse (mobile, /en/movies/the-odyssey) measured the result as
        LCP 13.3s of which 7.2s was "Load Delay" — the image simply had not been
        requested yet.

        Preloading declares it in the initial HTML so the fetch starts with the
        document instead of after the CSS. There is one preload per breakpoint,
        each with exactly the URL its media query paints below, so the browser
        fetches the cover once.
        `fetchPriority` is spelled lowercase and spread in because React 18.2
        does not map the camelCase prop — the same reason noted on the homepage
        hero in pages/index.tsx.
      */}
      {coverMobileUrl && coverDesktopUrl && (
        <Head>
          <link
            rel='preload'
            as='image'
            href={coverMobileUrl}
            media='(max-width: 1023.95px)'
            {...({ fetchpriority: 'high' } as Record<string, string>)}
          />
          <link
            rel='preload'
            as='image'
            href={coverDesktopUrl}
            media='(min-width: 1024px)'
            {...({ fetchpriority: 'high' } as Record<string, string>)}
          />
        </Head>
      )}
      <Box
        component='div'
        sx={{
          height: {
            mobile: 'calc(50vh - 56px)',
            laptop: 'calc(100vh - 100px)',
            mediumDesktop: 'calc(100vh - 100px)',
          },
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          background: {
            mobile: coverBackground(coverMobileUrl),
            laptop: coverBackground(coverDesktopUrl),
          },
          backgroundPosition: 'top center',
          backgroundSize: 'cover',
          overflow: 'hidden',
        }}
      >
        <Box
          component='div'
          sx={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            zIndex: 3,
          }}
        >
          <React.Fragment key='bottom'>
            <Button
              onClick={toggleDrawer(true)}
              variant='contained'
              sx={{
                fontSize: '14px',
                width: '108px',
                height: '40px',
                background: theme.palette.lightGrey.main,
                color: theme.palette.grey[80],
                fontWeight: 700,
                p: 0,
                display: 'none',
                '@keyframes translateInfo': {
                  '0%': {
                    opacity: 1,
                  },
                  '100%': {
                    opacity: 0,
                  },
                },

                [theme.breakpoints.down('laptop')]: {
                  display: playState ? 'none' : 'initial',
                },
              }}
            >
              <FormattedMessage id='movie_details.more_info' />
            </Button>
            <Drawer anchor='bottom' open={drawerState} onClose={toggleDrawer(false)}>
              <Box
                component='div'
                sx={{
                  pl: '30px',
                  py: '38px',
                  position: 'relative',
                  display: 'inline',
                  width: '70px',
                  height: '66px',
                }}
                onClick={toggleDrawer(false)}
              >
                <CloseButton />
              </Box>
              <MovieInfo data={movieBannerProps.data} />
            </Drawer>
          </React.Fragment>
        </Box>

        <Box
          component='div'
          sx={{
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            position: 'absolute',
            top: '0',
          }}
          onClick={checkPlaying}
        >
          <Box
            component='div'
            sx={{
              '& ::after': {
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: playState ? '10' : '1',
                width: playState ? '0%' : '100%',
                height: videoId ? '100%' : '0',
                content: "''",
                backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.87) 7.33%, 
                    rgba(0, 0, 0, 0) 47.54%), linear-gradient(256.27deg, #171A1F 0%, rgba(23, 26, 31, 0) 107.57%)`,

                animation: playState
                  ? `translateInfo 1500ms ${theme.transitions.easing.easeInOut} both `
                  : '',
              },
              '--plyr-color-main': theme.palette.primary.main,
              '& .plyr__poster': {},
              '& .plyr--video': {
                height: {
                  mobile: 'calc(50vh - 56px)',
                  laptop: 'calc(100vh - 100px)',
                  mediumDesktop: 'calc(100vh - 100px)',
                },
                '& .plyr__control.plyr__control--overlaid': {
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(24px)',
                  width: { laptop: pxToRem(156), tablet: '58px' },
                  height: { laptop: pxToRem(156), tablet: '58px' },
                  display: { laptop: 'flex', tablet: 'none', mobile: 'none' },
                  alignItems: 'center',
                  justifyContent: 'center',
                },

                '& .plyr__control--overlaid': {
                  [theme.breakpoints.down(460)]: {
                    right: '25px',
                    left: 'auto',
                  },
                },
              },
              '.plyr__controls': {
                display: !playState ? 'none !important' : 'flex',
              },
              '& .plyr__control::after': {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '0%',
                height: '0%',
              },

              '&  .plyr__control--overlaid svg': {
                width: `${pxToRem(37)} !important`,
                height: `${pxToRem(37)} !important`,
              },
            }}
          >
            {videoId && isMounted && (
              <Box className='plyr__video-embed' id='player'>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <Box
                  component='iframe'
                  src={handleTrailerURL(videoId)}
                  allowFullScreen
                  allowtransparency='true'
                  allow='autoplay'
                  ref={ref}
                />
              </Box>
            )}
          </Box>
        </Box>

        {movieBannerProps?.data?.film?.shareLink && (
          <IconButton
            aria-label='share'
            sx={{
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.5)',
              borderRadius: '12px',
              ml: pxToRem(51),
              position: 'absolute',
              top: '16px',
              right: '16px',
              display: 'none',
              [theme.breakpoints.down('laptop')]: {
                display: 'flex',
              },
              animation: playState
                ? `translateInfo 1500ms ${theme.transitions.easing.easeInOut} both `
                : '',
            }}
            onClick={() => {
              navigator.clipboard.writeText(movieBannerProps?.data?.film?.shareLink);
              setSnack({
                isOpen: true,
                message: 'movie_details.copied_link_successfully',
                icon: <Box component='img' src={copiedIcon} />,
                type: 'success',
              });
              closeCurrentDialog();
            }}
          >
            <ImageBoxFuture
              img={shareWhiteIcon}
              title={intl.formatMessage({
                id: 'alt.share_icon',
              })}
              objectFit='contain'
              sx={{
                width: '24px',
                height: '24px',
              }}
            />
          </IconButton>
        )}

        {!(ref.current?.plyr as Plyr)?.playing && videoId && (
          <IconButton
            aria-label='play'
            sx={{
              width: '58px',
              height: '58px',
              background: 'rgba(255, 255, 255, 0.5)',
              borderRadius: '100%',
              ml: pxToRem(51),
              position: 'absolute',
              top: `calc(50% - 29px)`,
              right: 'calc(50% - 29px)',
              display: 'none',
              zIndex: 100,
              [theme.breakpoints.down('laptop')]: {
                display: 'flex',
              },
            }}
            onClick={checkPlaying2}
          >
            <PlayArrowIcon
              sx={{ width: '29px', height: '29px', color: theme.palette.common.white }}
            />
          </IconButton>
        )}

        <Box
          component='div'
          sx={{
            color: theme.palette.common.white,
            pt: pxToRem(1),

            alignItems: 'flex-start',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            display: 'flex',
            maxWidth: { tablet: '42%', mobile: '100%' },
            [theme.breakpoints.down('laptop')]: {
              justifyContent: 'flex-end',
            },

            transform: 'translateX(0%)',
            animation: playState
              ? `translateLeft 2000ms ${theme.transitions.easing.easeInOut} both `
              : '',
            '@keyframes translateLeft': {
              '0%': {
                display: 'block',
                opacity: 1,
                transform: 'translateX(0%)',
              },
              '100%': {
                opacity: 0,
                transform: 'translateX(-100%)',
                display: 'none',
              },
            },
          }}
        >
          {movieBannerProps?.data?.film?.bookmark && (
            <Box
              component='span'
              sx={{
                height: pxToRem(44),
                p: 1,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: ' 0px 20px 20px 0px',
                fontSize: pxToRem(16),
                mt: pxToRem(239),
                display: 'inlineFlex',
                color: movieBannerProps?.data?.film?.bookmark?.fontColor,
                backgroundColor: movieBannerProps?.data?.film?.bookmark?.backgroundColor,
              }}
            >
              <BookmarkName name={movieBannerProps?.data?.film?.bookmark?.name} />
            </Box>
          )}

          <Box
            component='div'
            sx={{
              ml: pxToRem(68),
              mt: movieBannerProps?.data?.film?.bookmark ? '121px' : 0,
              display: 'initial',

              [theme.breakpoints.down('laptop')]: {
                mt: '48px',
                mb: '12px',
                ml: '20px',
              },
            }}
          >
            <Box component='div' sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component='h1'
                sx={{
                  fontSize: pxToRem(30),
                  fontWeight: '700',
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {movieBannerProps?.data?.film?.title}
              </Typography>

              {movieBannerProps?.data?.film?.shareLink && (
                <IconButton
                  aria-label='share'
                  sx={{
                    width: '68px',
                    height: '68px',
                    background: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '12px',
                    ml: pxToRem(51),
                    [theme.breakpoints.down('laptop')]: {
                      display: 'none',
                    },
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      movieBannerProps?.data?.film?.shareLink
                    );
                    setSnack({
                      isOpen: true,
                      message: 'movie_details.copied_link_successfully',
                      icon: <Box component='img' src={copiedIcon}></Box>,
                      type: 'success',
                    });
                    closeCurrentDialog();
                  }}
                >
                  <ImageBoxFuture
                    img={shareWhiteIcon}
                    title={intl.formatMessage({
                      id: 'alt.share_icon',
                    })}
                    objectFit='contain'
                    sx={{
                      width: '24px',
                      height: '24px',
                    }}
                  />
                </IconButton>
              )}
            </Box>
            <Box
              component='div'
              sx={{
                display: 'flex',
                mt: { laptop: pxToRem(11), tablet: '20px' },
                alignItems: 'center',
              }}
            >
              {formattedDuration && (
                <IconButton aria-label='time' sx={{}}>
                  <ImageBoxFuture
                    img={timeIcon}
                    title={intl.formatMessage({
                      id: 'alt.time_icon',
                    })}
                    objectFit='contain'
                    sx={{
                      width: pxToRem(28),
                      height: pxToRem(28),
                    }}
                  />
                </IconButton>
              )}
              <Typography component='p' sx={{ fontSize: pxToRem(24) }}>
                {durationWithRateWithRtl}
              </Typography>
            </Box>

            <Typography
              component='span'
              sx={{ fontSize: pxToRem(30), mt: { laptop: pxToRem(11), tablet: '4px' } }}
            >
              {movieBannerProps?.data?.film?.genres
                .slice(0, 2)
                .map(
                  (genre: Genres, index: number) =>
                    `${genre.name} ${index !== 1 ? ' . ' : ''}`
                )}
            </Typography>
          </Box>
        </Box>

        {movieBannerProps?.data?.film?.image && (
          <ImageBoxFuture
            img={movieBannerProps?.data?.film?.image}
            title={
              movieBannerProps?.data?.film?.imageAlt ||
              movieBannerProps?.data?.film?.title
            }
            // Shown at 570 px, laptop and up only; `responsive` + `sizes` instead
            // of an intrinsic srcset built from the declared 1600 px.
            layout='responsive'
            sizes='570px'
            objectFit='cover'
            width='1600px'
            height='2400px'
            sx={{
              width: pxToRem(570),
              borderRadius: '20px',
              overflow: 'hidden',
              marginRight: pxToRem(65),
              position: 'relative',
              [theme.breakpoints.down('laptop')]: {
                display: 'none',
              },
              transform: 'translateX(0%)',
              animation: playState
                ? `translateRight 2000ms ${theme.transitions.easing.easeInOut} both `
                : '',
              '@keyframes translateRight': {
                '0%': {
                  display: 'flex',
                  opacity: 1,
                  transform: 'translateX(0%)',
                },
                '100%': {
                  opacity: 0,
                  transform: 'translateX(+100%)',
                  display: 'none !important',
                },
              },
            }}
          />
        )}
      </Box>
    </>
  );
};

export default MovieBanner;
